import { useMemo, useState } from 'react'
import { SlideOver } from './SlideOver.js'
import { applySupplierQuote, previewSupplierApply, type SupplierApplyLine, type SupplierApplyPreview, type SupplierReprice } from '../lib/api.js'
import { DEFAULT_IMPORT_MARGIN_PCT, marginOnSell, sellFromCost } from '@shared/supplierQuote'
import { formatUKDate } from '@shared/dates'

/**
 * Decision 212 — apply a distributor quote to an EXISTING draft. Upload the
 * new Ingram workbook; the sheet's lines are matched to the quote's by SKU:
 * matched lines get the new COST (and, only if you say so, a new sell price
 * at the margin — Mark: "when a cost changes, ASK if the quote is to be
 * repriced or not"); unmatched SKUs are ADDED as new lines (products created
 * in HubSpot as the import does); quote lines the sheet no longer carries
 * are listed and left alone. Works on any internal draft — it need not have
 * come from an import.
 */

type Override = { qty?: number | null; sellPrice?: number | null; include?: boolean }
const gbp = (n: number | null) => n == null ? '—' : `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

export function SupplierQuoteApply({ quoteId, quoteNumber, defaultMarginPct, onClose, onApplied, onError }: {
  quoteId: string
  quoteNumber: string
  /** The quote's current margin, so re-pricing starts from what it already earns. */
  defaultMarginPct: number | null
  onClose: () => void
  onApplied: (notice: string) => void
  onError: (message: string) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [marginPct, setMarginPct] = useState<string>(String(defaultMarginPct != null && defaultMarginPct > 0 && defaultMarginPct < 100 ? Math.round(defaultMarginPct * 10) / 10 : DEFAULT_IMPORT_MARGIN_PCT))
  const [reprice, setReprice] = useState<SupplierReprice | null>(null)   // the question — must be answered
  const [reference, setReference] = useState('')
  const [groupId, setGroupId] = useState<string>('')                     // '' = a new section for added lines
  const [preview, setPreview] = useState<SupplierApplyPreview | null>(null)
  const [overrides, setOverrides] = useState<Record<string, Override>>({})
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const margin = Number(marginPct)
  const marginOk = Number.isFinite(margin) && margin >= 0 && margin < 100

  // Local re-computation so the margin box and the reprice answer update the
  // table without another upload (the apply sends both again anyway).
  const lines = useMemo(() => {
    if (!preview) return []
    return preview.lines.map((l) => {
      const o = overrides[l.lineRef] ?? {}
      const newQty = o.qty != null && o.qty > 0 ? o.qty : (l.action === 'update' ? l.currentQty ?? l.qty : l.qty)
      const newSell = o.sellPrice != null ? o.sellPrice
        : l.action === 'update' && reprice !== 'margin' ? (l.currentSell ?? l.newSell)
        : (marginOk ? sellFromCost(l.unitCost, margin) : l.newSell)
      return { ...l, newQty, newSell, include: o.include !== false }
    })
  }, [preview, overrides, margin, marginOk, reprice])

  const totals = useMemo(() => {
    if (!preview) return null
    const inc = lines.filter((l) => l.include)
    const touched = new Set(inc.map((l) => l.itemId).filter(Boolean))
    // Before − the touched lines' old values + their new values (untouched lines are constant).
    const oldTouchedCost = inc.filter((l) => l.itemId).reduce((s, l) => s + (l.currentQty ?? 0) * (l.currentCost ?? 0), 0)
    const oldTouchedSell = inc.filter((l) => l.itemId).reduce((s, l) => s + (l.currentQty ?? 0) * (l.currentSell ?? 0), 0)
    const costAfter = preview.totals.costBefore - oldTouchedCost + inc.reduce((s, l) => s + l.newQty * l.unitCost, 0)
    const sellAfter = preview.totals.sellBefore - oldTouchedSell + inc.reduce((s, l) => s + l.newQty * l.newSell, 0)
    void touched
    return {
      costAfter, sellAfter, marginAfter: marginOnSell(costAfter, sellAfter),
      updates: inc.filter((l) => l.action === 'update').length, adds: inc.filter((l) => l.action === 'add').length,
      toCreate: inc.filter((l) => l.status === 'new').length,
      costChanges: inc.filter((l) => l.action === 'update' && l.currentCost != null && Math.abs(l.currentCost - l.unitCost) >= 0.005).length,
    }
  }, [preview, lines])

  const overrideList = () => Object.entries(overrides).map(([lineRef, o]) => ({ lineRef, ...o }))

  const runPreview = async () => {
    if (!file) { setProblem('Choose the distributor quote (.xlsx) first.'); return }
    if (!marginOk) { setProblem('Margin must be from 0% up to (not including) 100%.'); return }
    setBusy('preview'); setProblem(null)
    try {
      const p = await previewSupplierApply(quoteId, file, margin, reprice ?? 'keep', overrideList())
      setPreview(p)
      if (p.header.reference) setReference(p.header.reference)
    } catch (e) { setProblem((e as Error).message) } finally { setBusy(null) }
  }

  const runApply = async () => {
    if (!file || !preview || !totals) return
    if (!marginOk) { setProblem('Margin must be from 0% up to (not including) 100%.'); return }
    if (!reference.trim()) { setProblem('The sheet carries no quote reference — type one.'); return }
    if (totals.updates + totals.adds === 0) { setProblem('Every line is excluded — nothing to apply.'); return }
    if (totals.costChanges > 0 && reprice === null) { setProblem('Costs are changing — say whether the quote is to be re-priced or not.'); return }
    if (totals.toCreate > 0 && preview.writesDisabled) { setProblem('HubSpot writes are off on this deploy — the new SKUs cannot be created.'); return }
    const ok = window.confirm(
      `Apply ${preview.header.distributor} quote ${reference.trim()} to ${quoteNumber}?\n\n`
      + `• ${totals.updates} line(s) updated (${totals.costChanges} cost change${totals.costChanges === 1 ? '' : 's'}) — ${reprice === 'margin' ? `re-priced at ${margin}% margin` : 'sell prices KEPT as quoted'}\n`
      + `• ${totals.adds} line(s) added${groupId ? ` into “${preview.groups.find((g) => g.id === groupId)?.name ?? 'the chosen section'}”` : ' into a new section'}\n`
      + (totals.toCreate > 0 ? `• ${totals.toCreate} new product(s) created in HubSpot\n` : '')
      + (preview.missing.length > 0 ? `• ${preview.missing.length} quote line(s) not on the sheet are left as they are\n` : '')
      + `• quote total ${gbp(preview.totals.sellBefore)} → ${gbp(totals.sellAfter)} · margin ${preview.totals.marginBefore ?? '—'}% → ${totals.marginAfter ?? '—'}%\n`
      + `• the workbook is saved with the quote and logged as cost evidence`,
    )
    if (!ok) return
    setBusy('apply'); setProblem(null)
    try {
      const r = await applySupplierQuote(quoteId, file, margin, reprice ?? 'keep', overrideList(), reference.trim(), groupId || null)
      const notes: string[] = []
      if (r.productsCreated.length > 0) notes.push(`created ${r.productsCreated.length} product(s) in HubSpot: ${r.productsCreated.join(', ')}`)
      for (const w of r.warnings) notes.push(w)
      if (notes.length > 0) window.alert(`${preview.header.distributor} quote ${r.reference} applied to ${quoteNumber} — please review:\n\n• ${notes.join('\n• ')}`)
      onApplied(`Applied ${preview.header.distributor} quote ${r.reference}: ${r.updated} line(s) updated, ${r.added} added${r.missing > 0 ? `, ${r.missing} not on the sheet left as they were` : ''} — ${reprice === 'margin' ? `re-priced at ${margin}%` : 'sell prices kept'}.`)
    } catch (e) { onError((e as Error).message); setBusy(null) }
  }

  const setOv = (lineRef: string, patch: Override) => setOverrides((cur) => ({ ...cur, [lineRef]: { ...(cur[lineRef] ?? {}), ...patch } }))

  const actionPill = (l: SupplierApplyLine) => {
    if (l.action === 'add') {
      return l.status === 'new'
        ? <span className="pill pill--warn" title="Not on the quote and not in the HubSpot price book — added as a new line, product created (cloned from MS150-24MP-4X)">add · new product</span>
        : <span className="pill pill--info" title="Not on the quote — added as a new line">add</span>
    }
    const changed = l.currentCost != null && Math.abs(l.currentCost - l.unitCost) >= 0.005
    return changed
      ? <span className="pill pill--ok" title={`On the quote in “${l.sectionName}” — cost ${gbp(l.currentCost)} → ${gbp(l.unitCost)}`}>update cost</span>
      : <span className="pill" title={`On the quote in “${l.sectionName}” — same cost`}>same cost</span>
  }

  return (
    <SlideOver title={`Apply a distributor quote to ${quoteNumber}`} subtitle="Matched SKUs take the new cost; new SKUs are added; you decide whether sell prices move." onClose={onClose} wide>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
        <div className="field">
          <label htmlFor="apply-file">Distributor quote (.xlsx)</label>
          <input id="apply-file" className="input" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" data-testid="apply-file" style={{ width: '100%' }}
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setOverrides({}) }} />
        </div>
        <div className="field">
          <label htmlFor="apply-margin">Margin on sell price %</label>
          <input id="apply-margin" className="input" type="number" min={0} max={99.99} step={0.5} value={marginPct} data-testid="apply-margin"
            onChange={(e) => setMarginPct(e.target.value)} style={{ width: 110 }} />
          <div className="hint">used for added lines, and for updated lines if you re-price</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <button className="btn" disabled={busy !== null || !file} onClick={() => void runPreview()} data-testid="apply-preview">
          {busy === 'preview' ? 'Reading…' : preview ? 'Re-read the sheet' : 'Read the sheet'}
        </button>
        {preview && <span className="muted small">{preview.filename}</span>}
      </div>
      {problem && <div className="banner banner--warn" style={{ marginBottom: 10 }}>⚠ {problem}</div>}

      {preview && totals && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 16px', marginBottom: 10 }} data-testid="apply-header">
            <div>
              <span className="muted small">Quote reference</span>
              <div><input className="input" value={reference} onChange={(e) => setReference(e.target.value)} style={{ width: 180 }} data-testid="apply-reference" /></div>
            </div>
            <div><span className="muted small">Price protection</span><div>{preview.header.priceProtectionText ?? '—'}</div></div>
            <div><span className="muted small">Quoted</span><div>{preview.header.quotedOn ? formatUKDate(preview.header.quotedOn) : '—'}{preview.header.salesperson ? ` · ${preview.header.salesperson}` : ''}</div></div>
          </div>
          {preview.warnings.map((w, i) => <div key={i} className="banner banner--warn" style={{ marginBottom: 6 }}>⚠ {w}</div>)}

          {/* THE question — asked whenever a matched line's cost changes. */}
          {totals.costChanges > 0 && (
            <div className={`banner${reprice === null ? ' banner--warn' : ''}`} style={{ marginBottom: 10, display: 'block' }} data-testid="apply-reprice">
              <strong>{totals.costChanges} line{totals.costChanges === 1 ? "'s cost changes" : "s' costs change"} — is the quote to be re-priced?</strong>
              <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                <label><input type="radio" name="reprice" checked={reprice === 'keep'} onChange={() => setReprice('keep')} /> <b>No</b> — keep every sell price as quoted (the margin moves)</label>
                <label><input type="radio" name="reprice" checked={reprice === 'margin'} onChange={() => setReprice('margin')} /> <b>Yes</b> — re-price the updated lines at {marginOk ? `${margin}%` : 'the margin'} on sell</label>
              </div>
            </div>
          )}
          {totals.adds > 0 && (
            <div className="field" style={{ marginBottom: 10 }}>
              <label htmlFor="apply-group">Section for the {totals.adds} added line{totals.adds === 1 ? '' : 's'}</label>
              <select id="apply-group" className="select" value={groupId} onChange={(e) => setGroupId(e.target.value)} data-testid="apply-group" style={{ maxWidth: 360 }}>
                <option value="">New section — {preview.header.distributor} — {reference || 'quote'}</option>
                {preview.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
          )}

          <div className="table-wrap">
            <table data-testid="apply-lines">
              <thead>
                <tr><th></th><th>#</th><th>SKU</th><th>Description</th><th>Qty</th><th>Cost</th><th>Sell</th><th>Margin</th><th>Action</th></tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.lineRef} className={l.include ? undefined : 'muted'} data-testid={`apply-line-${l.lineRef}`}>
                    <td><input type="checkbox" checked={l.include} onChange={(e) => setOv(l.lineRef, { include: e.target.checked })} aria-label={`Include ${l.sku}`} /></td>
                    <td className="muted small">{l.lineRef}</td>
                    <td><code>{l.sku}</code></td>
                    <td>{l.description}{l.sectionName && <div className="muted small">in “{l.sectionName}”</div>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <input className="input" type="number" min={0.01} step={1} value={l.newQty} style={{ width: 70 }}
                        onChange={(e) => setOv(l.lineRef, { qty: Number(e.target.value) || null })} aria-label={`Quantity for ${l.sku}`} />
                      {l.action === 'update' && l.currentQty != null && l.qty !== l.currentQty && (
                        <div className="muted small">sheet says {l.qty} · <a href="#" onClick={(e) => { e.preventDefault(); setOv(l.lineRef, { qty: l.qty }) }}>use it</a></div>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {l.action === 'update' && l.currentCost != null && Math.abs(l.currentCost - l.unitCost) >= 0.005
                        ? <><s className="muted">{gbp(l.currentCost)}</s> → <strong>{gbp(l.unitCost)}</strong></>
                        : gbp(l.unitCost)}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <input className="input" type="number" min={0} step={0.01} value={l.newSell} style={{ width: 100 }}
                        onChange={(e) => setOv(l.lineRef, { sellPrice: e.target.value === '' ? null : Number(e.target.value) })} aria-label={`Sell price for ${l.sku}`} />
                      {l.action === 'update' && l.currentSell != null && Math.abs(l.currentSell - l.newSell) >= 0.005 && <div className="muted small">was {gbp(l.currentSell)}</div>}
                    </td>
                    <td className="muted small">{l.unitCost === 0 ? '—' : `${marginOnSell(l.unitCost, l.newSell) ?? 0}%`}</td>
                    <td>{actionPill(l)}</td>
                  </tr>
                ))}
                {preview.missing.map((m) => (
                  <tr key={m.itemId} className="muted" data-testid={`apply-missing-${m.itemId}`}>
                    <td></td><td></td>
                    <td><code>{m.sku ?? '—'}</code></td>
                    <td>{m.name}<div className="small">in “{m.sectionName}”</div></td>
                    <td>{m.qty}</td>
                    <td>{gbp(m.cost)}</td>
                    <td colSpan={2}></td>
                    <td><span className="pill" title="On the quote but not on this sheet — left exactly as it is">not on sheet · unchanged</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5}><strong>{totals.updates} updated · {totals.adds} added</strong>{totals.toCreate > 0 ? <span className="muted small"> · {totals.toCreate} to create in HubSpot</span> : null}{preview.missing.length > 0 ? <span className="muted small"> · {preview.missing.length} unchanged</span> : null}</td>
                  <td><span className="muted small">{gbp(preview.totals.costBefore)} →</span> <strong>{gbp(totals.costAfter)}</strong></td>
                  <td><span className="muted small">{gbp(preview.totals.sellBefore)} →</span> <strong>{gbp(totals.sellAfter)}</strong></td>
                  <td>{preview.totals.marginBefore ?? '—'}% → {totals.marginAfter ?? '—'}%</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn btn--primary" disabled={busy !== null || totals.updates + totals.adds === 0} onClick={() => void runApply()} data-testid="apply-go">
              {busy === 'apply' ? 'Applying…' : 'Apply to the quote'}
            </button>
            <span className="muted small" style={{ alignSelf: 'center' }}>Drafts only — a sent quote is superseded first.</span>
          </div>
        </>
      )}
    </SlideOver>
  )
}
